import React from 'react';
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material';

import { type HomeQueueTile, oldestLabel } from './homeQueueTiles';

/**
 * One queue in Home's summary strip (GDN-4085): its rail, its name, its
 * count, and one line under the count. The line is the age of the queue's
 * oldest claim, "All clear" when the queue is empty, or what went wrong.
 *
 * Presentational, and a real `tab`: the strip owns the tablist, the roving
 * tab stop and the arrow keys, and the container owns which tile is selected.
 * Every element inside is a span, because a button holds phrasing content
 * only.
 */
export interface QueueTileProps {
  tile: HomeQueueTile;
  /** The tab's button, for the strip to move focus to. */
  ref?: React.Ref<HTMLButtonElement>;
  /** The tab's id, which the panel names as its label. */
  id: string;
  /** The panel this tab controls. */
  panelId: string;
  /** Roving tab stop: 0 on the one tab the Tab key lands on, -1 on the rest. */
  tabIndex: 0 | -1;
  onSelect: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

/**
 * The count, or a placeholder while nothing has said it. Each placeholder
 * sits inside its line's own Typography, which is where a text Skeleton
 * takes its height from, so the tile keeps its height when the number lands.
 */
const TileCount: React.FC<{ tile: HomeQueueTile }> = ({ tile }) => {
  if (tile.count === undefined && tile.loading) {
    return (
      <Typography component="span" sx={{ display: 'block', lineHeight: 1 }} variant="h4">
        <Skeleton width={40} />
      </Typography>
    );
  }
  return (
    <Typography
      component="span"
      data-testid={`home-queue-tile-count-${tile.queue.key}`}
      sx={{
        color: tile.clear ? 'grey.600' : tile.queue.color,
        display: 'block',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 700,
        lineHeight: 1,
      }}
      variant="h4"
    >
      {/* A failed read with no count is not a zero. */}
      {tile.count ?? '-'}
    </Typography>
  );
};

/** The line under the count. */
const TileStatus: React.FC<{ tile: HomeQueueTile }> = ({ tile }) => {
  if (tile.clear) {
    return (
      <Typography
        component="span"
        sx={{ color: 'success.dark', display: 'block', fontWeight: 700 }}
        variant="caption"
      >
        All clear
      </Typography>
    );
  }
  if (tile.failed) {
    return (
      <Typography component="span" sx={{ color: 'error.dark', display: 'block' }} variant="caption">
        Could not load
      </Typography>
    );
  }
  if (tile.loading) {
    return (
      <Typography component="span" sx={{ display: 'block' }} variant="caption">
        <Skeleton width="60%" />
      </Typography>
    );
  }
  if (tile.oldestDays === null) return null;
  return (
    <Typography color="text.secondary" component="span" sx={{ display: 'block' }} variant="caption">
      {oldestLabel(tile.oldestDays)}
    </Typography>
  );
};

export const QueueTile: React.FC<QueueTileProps> = ({
  tile,
  ref,
  id,
  panelId,
  tabIndex,
  onSelect,
  onKeyDown,
}) => (
  <ButtonBase
    ref={ref}
    aria-controls={panelId}
    aria-selected={tile.selected}
    data-testid={`home-queue-tile-${tile.queue.key}`}
    disableRipple
    id={id}
    onClick={onSelect}
    onKeyDown={onKeyDown}
    role="tab"
    sx={{
      alignItems: 'stretch',
      bgcolor: tile.selected ? 'backgroundColors.secondary' : 'background.paper',
      border: '1px solid',
      borderColor: tile.selected ? 'primary.main' : 'divider',
      borderRadius: '10px',
      /* The ring doubles the selected border without moving the content. */
      boxShadow: theme => (tile.selected ? `0 0 0 1px ${theme.palette.primary.main}` : 'none'),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      minWidth: 0,
      overflow: 'clip',
      textAlign: 'left',
      '&:hover': { borderColor: tile.selected ? 'primary.main' : 'grey.400' },
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
    }}
    tabIndex={tabIndex}
  >
    <Box
      aria-hidden
      component="span"
      sx={{ bgcolor: tile.queue.color, display: 'block', height: 4 }}
    />
    <Box
      component="span"
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pb: 1.75, pt: 1.5, px: 2 }}
    >
      <Typography
        component="span"
        noWrap
        sx={{ display: 'block', fontWeight: 700 }}
        variant="body2"
      >
        {tile.queue.label}
      </Typography>
      <TileCount tile={tile} />
      <TileStatus tile={tile} />
    </Box>
  </ButtonBase>
);
